import { useState } from 'react';
import { CoachService } from '../../services/coach';
import { Alert, Badge, Button, Icon, Modal, Spinner } from '../atoms';
import { FormField } from '../molecules';
import { MarkdownText } from '../molecules/MarkdownText';
import { ChatWidgetRenderer } from '../widgets/ChatWidget';
import type { ChatMessage } from '../../types/coach';

interface FeedbackButtonProps {
  messageId: string;
  userId: string;
  sessionId?: string;
  message?: ChatMessage;
}

export function FeedbackButton({ messageId, userId, sessionId, message }: FeedbackButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim() || isSubmitting) {return;}

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await CoachService.submitFeedback(
        messageId,
        feedback.trim(),
        userId,
        sessionId
      );

      if (result.success) {
        setIsSubmitted(true);
        setFeedback('');
        setIsOpen(false);
        // Auto-hide success state after 3 seconds
        setTimeout(() => setIsSubmitted(false), 3000);
      } else {
        setError(result.error ?? 'Failed to submit feedback');
      }
    } catch (_err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setFeedback('');
    setError(null);
  };

  // No manual escape handling needed - Modal component handles this

  if (isSubmitted) {
    return (
      <Badge 
        variant="success" 
        size="xs"
        icon={<Icon name="check" size="xs" />}
      >
        Feedback submitted
      </Badge>
    );
  }

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="neutral"
        size="xsmall"
        title="Provide feedback on this response"
        icon={<Icon name="chat" size="sm" />}
        iconPosition="left"
      >
        Feedback
      </Button>

      <Modal 
        isOpen={isOpen} 
        onClose={handleCancel} 
        size="xl" 
        className="max-w-6xl h-[90vh] flex flex-col"
      >
            {/* Content */}
            <div className="flex flex-col lg:flex-row max-h-[90vh]">
              {/* Left Panel - AI Response */}
              {message && (
                <div className="flex-1 lg:border-r border-border-separator">
                  <div className="flex items-center justify-between p-4 md:p-6 border-b border-border-separator min-h-[100px]">
                    <div>
                      <h3 className="text-base md:text-lg font-medium text-text-primary mb-1">
                        AI Response
                      </h3>
                      <p className="text-sm text-text-muted">
                        Review the response you&apos;re providing feedback on
                      </p>
                    </div>
                    {/* Empty div to match the structure and maintain alignment */}
                    <div className="w-14 h-14" />
                  </div>
                  <div className="p-4 md:p-6 overflow-y-auto max-h-[40vh] lg:max-h-[calc(90vh-140px)] custom-scrollbar">
                    <div className="bg-bg-primary rounded-lg border border-border-light p-3 md:p-4">
                      <div className="space-y-3 text-text-primary">
                        {message.widgets?.map((widget, widgetIndex) => (
                          <div key={`feedback-widget-${widgetIndex}`}>
                            {widget.type === 'text' ? (
                              <MarkdownText className="text-sm leading-relaxed text-text-primary">
                                {widget.text}
                              </MarkdownText>
                            ) : (
                              <ChatWidgetRenderer widget={widget} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Right Panel - Feedback Form */}
              <div className={`${message ? 'flex-1' : 'w-full'}`}>
                {/* Feedback Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-border-separator min-h-[100px]">
                  <div>
                    <h3 className="text-base md:text-lg font-medium text-text-primary mb-1">
                      Share Your Feedback
                    </h3>
                    <p className="text-sm text-text-muted">
                      Help us improve by sharing your thoughts on this response
                    </p>
                  </div>
                  <Button
                    onClick={handleCancel}
                    variant="neutral"
                    size="medium"
                    title="Close feedback modal"
                    iconOnly
                    icon={<Icon name="close" size="md" />}
                  />
                </div>

                <form onSubmit={(e) => void handleSubmit(e)} className="p-4 md:p-6 flex flex-col min-h-0 flex-1">
                  <div className="flex-1 space-y-4">
                    <div>
                      <FormField
                        id={`feedback-${messageId}`}
                        label="What could be improved about this response?"
                        as="textarea"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value.slice(0, 1000))}
                        placeholder="Please share specific details about what you'd like to see improved. For example:&#10;• Was the response helpful or accurate?&#10;• Did it address your question fully?&#10;• Was the tone or style appropriate?&#10;• Any suggestions for better responses?"
                        autoResize={false}
                        minHeight={message ? 200 : 150}
                        maxHeight={300}
                        className="text-sm md:text-base font-manrope"
                      />
                      <div className="mt-2 text-xs text-text-muted">
                        {feedback.length}/1000 characters
                      </div>
                    </div>
                    
                    {error && (
                      <Alert variant="error" message={error} />
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-4 border-t border-border-separator">
                    <Button
                      type="button"
                      onClick={handleCancel}
                      variant="neutral"
                      size="medium"
                      state={isSubmitting ? 'disabled' : 'enabled'}
                      className="sm:w-auto w-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="medium"
                      state={(!feedback.trim() || isSubmitting) ? 'disabled' : 'enabled'}
                      icon={isSubmitting ? (
                        <Spinner size="sm" variant="white" />
                      ) : undefined}
                      iconPosition="left"
                      className="sm:w-auto w-full"
                    >
                      {isSubmitting ? 'Submitting Feedback...' : 'Submit Feedback'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
      </Modal>
    </>
  );
}